import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Login from '@components/pages/loginPage/components/Login';
import Tenant from '@components/pages/loginPage/components/Tenant';
import { AuthenticatedTemplate, UnauthenticatedTemplate, useIsAuthenticated, useMsal } from '@azure/msal-react';
import Main from '@components/primitives/main/Main';
import HublLogo from '@images/svg/hubl.svg?react';
import usePageTitle from '@hooks/usePageTitle';
import AppStoreBadge from '@images/appstore.svg?react';
import PlayStoreBadge from '@images/playstore.svg?react';
import useAuthentication from '@hooks/useAuthentication';
import { useLocation } from 'react-router-dom';
import { redirectAfterLoginAtom } from '@js/store/authenticationAtoms';
import { useSetAtom } from 'jotai';
import { buildVersion } from '@constants/buildVersion';
import OutdatedBrowserMessage from '@components/features/outdatedBrowser/components/OutdatedBrowserMessage';
import usePageDescription from '@hooks/usePageDescription';
import classNames from 'classnames';

const randomIndex = Math.floor(Math.random() * 10) + 1;

const LoginPage: FC = () => {
    const isAuthenticated = useIsAuthenticated();
    const { t } = useTranslation();
    const { azureLogout } = useAuthentication();
    const { accounts } = useMsal();
    const currentAccount = accounts.length ? accounts[0] : null;
    usePageTitle(t('login.title', { ns: 'authentication' }));
    usePageDescription(t('meta.description'));
    const setRedirectAfterLogin = useSetAtom(redirectAfterLoginAtom);
    const location = useLocation();

    const [bgImage, setBgImage] = useState<string>('');

    useEffect(() => {
        import(`@images/login/${randomIndex}.jpg`).then((module: { default: string }) => {
            setBgImage(module.default);
        });
    }, []);

    useEffect(() => {
        setRedirectAfterLogin(location?.state?.from);
    }, [location?.state?.from]);

    return (
        <>
            <Main hasCover>
                <OutdatedBrowserMessage />
                <section className="loginBox">
                    <div
                        className={classNames('loginBox__image', {
                            'loginBox__image--loaded': bgImage,
                        })}
                        style={{
                            backgroundImage: bgImage ? `url(${bgImage})` : undefined,
                        }}
                    />
                    <div className="loginBox__content">
                        <HublLogo className="loginBox__logo" />
                        <div>
                            <UnauthenticatedTemplate>
                                <Login />
                            </UnauthenticatedTemplate>
                            <AuthenticatedTemplate>
                                <Tenant />
                            </AuthenticatedTemplate>
                        </div>
                    </div>
                </section>
                <div className="loginBox__footer">
                    <div style={{ display: 'none' }}>Release: {buildVersion}</div>
                    {isAuthenticated && currentAccount ? (
                        <p data-testid="login-text-logged-in">
                            {t('login.textLoggedIn', { ns: 'authentication', username: currentAccount.username })}
                            &nbsp;-&nbsp;
                            <span onClick={azureLogout}>{t('logout', { ns: 'authentication' })}</span>
                        </p>
                    ) : null}
                    <a href="https://apps.apple.com/nl/app/whitevision/id1546713138" target="_blank" rel="noreferrer" data-testid="login-appstore-badge">
                        <AppStoreBadge className="storeBadge" />
                    </a>
                    <a
                        href="https://play.google.com/store/apps/details?id=nl.whitevision.whitevisionapp&hl=en&gl=US"
                        target="_blank"
                        rel="noreferrer"
                        data-testid="login-playstore-badge"
                    >
                        <PlayStoreBadge className="storeBadge" />
                    </a>
                </div>
            </Main>
        </>
    );
};

export default LoginPage;
